







import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { lazyInject } from '@/app/container';
import { TranslateResult } from 'vue-i18n';
import { TocItem } from '@/app/shared/models';
import {
  SectionProgressInterface,
  SectionProgressSymbol,
  SectionCompletion
} from '@/app/statistics/section/section-progress';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import {
  SectionInteractionEvent,
  SectionInteractedEventName
} from '@/app/statistics/events/section-interaction-events';
import {
  SectionScrollService,
  SectionScrollServiceSymbol
} from '@/app/statistics/section/section-scroll-service';
import { listenScroll } from '@/app/shared/utils/content-utils';
import { tocHrefPagesEquals } from '@/app/aspect/toc-utils';
import { initTermHints } from '@/app/shared/utils/term-utils';
import { setFocus } from '@/app/accessibility/accessibility-utils';
import sectionStoreModule from '@/app/statistics/state/section-state';
import VirtualMediaComponent from '@/app/presenter/components/virtual-media-component.vue';

@Component({
  components: {
    VirtualMediaComponent,
  }
})
export default class PageComponent extends Vue {
  @Prop()
  public page: string;

  @Prop()
  public content: string;

  @Prop()
  public currentSection: TocItem|null;

  @lazyInject(SectionProgressSymbol)
  private sectionProgressService: SectionProgressInterface;

  @lazyInject(SectionScrollServiceSymbol)
  private sectionScrollService: SectionScrollService;

  @lazyInject(EventStreamSymbol)
  private eventStream: EventStream;

  private readonly pageComponentsSelector: string = '.page.component';
  private readonly nextButtonCssClass: string = 'next-page';

  private visiblePageComponentIndex: number = -1;

  private isDestroyed: boolean = false

  public get mediaOnPage(): boolean {
    return $('video, audio', $(this.content)).length > 0;
  }

  private get navigatedSectionId(): string|null {
    return sectionStoreModule.navigatedSectionId;
  }

  private get nextButton(): string {
    const caption: TranslateResult = this.$t('player.continue');
    return `<button class="${this.nextButtonCssClass} button shadowed animated fadeInUp">${caption}</button>`;
    /*const button = new ContentLockButtonComponent({propsData: { label: caption, class: this.nextButtonCssClass }});*/
    /*return $element.append(button.$el); // todo: append component */
  }

  public beforeDestroy() {
    this.isDestroyed = true
  }

  public mounted(): void {

    this.hideAllPageComponents();
    // REVIEW: #complexity #timing why this timeout is necessary
    $(() => {
      setTimeout(async () => {

        if (this.isDestroyed) {
          return
        }

        if (this.currentSection === null) {
          console.error('CURRENT SECTION IS NULL')
          return
        }


          const sectionId = this.currentSection.id;
          const completion: SectionCompletion = await this.sectionProgressService.findOrCreate(sectionId);
          this.revealNecessaryPageComponents(completion);

        this.moveNextButtonBeforeHiddenPageComponent();
        this.registerNextButtonEventHandler();

        const sections = this.calculateSectionsOnPage();
        if (sections !== null) {
          // if the section have child sections
          // TODO: where to put?
          this.sectionScrollService.listenScroll(sections);
        } else {
          listenScroll(this.page, sectionId, this.eventStream);
        }

        this.scrollToSectionIfNecessary();

        initTermHints();

        this.setFocus();
      }, 200);
    });
  }

  @Watch('navigatedSectionId')
  public onNavigatedSectionId(newValue: string|null): void {
    if (newValue !== null) {
      this.scrollToSectionIfNecessary(newValue);
    }
  }

  private hideAllPageComponents(): void {
    $(this.pageComponentsSelector).hide().addClass('content-fade-in');
  }

  private revealNecessaryPageComponents(completion: SectionCompletion): void {
    $(this.pageComponentsSelector).each((index: number, element: any) => {
      if (completion.progress.page >= index) {
        $(element).show();
        this.visiblePageComponentIndex++;
      }
    });
  }

  private moveNextButtonBeforeHiddenPageComponent(): void {
    $(`.${this.nextButtonCssClass}`).remove();
    const firstHidden: any = this.firstHiddenPageComponent();
    if (firstHidden !== null) {
      firstHidden.before(this.nextButton);
    }
  }

  private registerNextButtonEventHandler(): void {
    const event: string = 'click.nextButton';
    $('.main-container').off(event).on(event, `.${this.nextButtonCssClass}`, () => {
      const firstHidden: any = this.firstHiddenPageComponent();
      if (firstHidden === null) {
        return;
      }

      firstHidden.show();
      this.moveNextButtonBeforeHiddenPageComponent();
      this.visiblePageComponentIndex++;

      this.eventStream.dispatch({
        name: SectionInteractedEventName,
        payload: {
          page: this.visiblePageComponentIndex
        }
      } as SectionInteractionEvent);
    });
  }

  private firstHiddenPageComponent(): any {
    return $(`${this.pageComponentsSelector}:hidden`).first();
  }

  private scrollToSectionIfNecessary(section: string|null = null): void {
    if (section === null) {
      section = this.$route.query.section as string;
      if (!section) {
        return;
      }
    }

    sectionStoreModule.setAutoScrollInProgress(true);

    const $section = $(`#${section}`);
    const visible: boolean = $section.is(':visible');
    if (!visible) {
      return;
    }

    const $scrollElement: any = $('.scrollable-box');

    const sectionOffset = $section.offset();
    const breadcrumbOffset = 50;
    let sectionOffsetTop = 0;
    if (sectionOffset) {
      sectionOffsetTop = sectionOffset.top;
    }

    $scrollElement.animate({
      scrollTop: sectionOffsetTop + $scrollElement.scrollTop() - $scrollElement.offset().top - breadcrumbOffset
    }, 300, () => {
      this.setFocus(section);
      setTimeout(() => sectionStoreModule.setAutoScrollInProgress(false), 200);
    });
  }

  private calculateSectionsOnPage(): TocItem[]|null {
    if (this.currentSection === null || !this.currentSection.children || !this.currentSection.href) {
      return null;
    }

    const results: TocItem[] = [
      this.currentSection
    ];

    if (this.currentSection.children) {
      return this.addToResults(this.currentSection, this.currentSection.children, results);
    }

    return results;
  }

  private addToResults(forSection: TocItem, sections: TocItem[], results: TocItem[]): TocItem[] {
    for (const section of sections) {
      if (tocHrefPagesEquals(section.href, forSection.href)) {
        results = [...results, section];
      }

      if (section.children) {
        results = this.addToResults(forSection, section.children, results);
      }
    }

    return results;
  }

  private setFocus(sectionId: string|null = null): void {
    if (sectionId === null) {
      sectionId = sectionStoreModule.currentSectionId;
    }

    setFocus(`#${sectionId}`, -1);
  }
}
